/**
 * Cross-org prayer unread badges.
 *
 * Web port of flock-expo/src/hooks/useAllGroupsPrayerUnread.ts — SAME
 * Firestore collections, so read state is shared with the mobile app:
 *
 *   prayerGroupActivity/{groupId}
 *     { recentActivities: [{ prayerId, type, privacyLevel, at: ISO, userId? }] }
 *     (written by the backend on new/encouraged/answered; capped at 50)
 *
 *   prayer_read_receipts/{firebaseUid}_{groupId}
 *     { userId, groupId, lastSeenAt: ISO, updatedAt: serverTimestamp }
 *
 * Badge per group = unique prayerIds with visible activity newer than the
 * receipt: own actions never badge, `private` never badges others,
 * `leaders_only` badges only leaders of that group's org.
 *
 * Listener topology: 2 doc listeners per group across ALL orgs (both
 * collections are read-if-authenticated, not org-claim gated, so no
 * claimsReady gating is needed). Re-subscribes when the tab becomes
 * visible again (same hardening as useAllConversations).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { auth, db } from '@/config/firebase';
import { useAuth } from '@/context/AuthContext';

const PRAYER_GROUP_ACTIVITY_COLLECTION = 'prayerGroupActivity';
const PRAYER_READ_RECEIPTS_COLLECTION = 'prayer_read_receipts';

const LEADER_ROLES = ['org_admin', 'campus_pastor', 'ministry_director', 'group_leader'];

interface PrayerActivity {
  prayerId: string;
  type: 'new' | 'encouraged' | 'answered';
  privacyLevel: string;
  at: string;
  userId?: string; // Firebase UID of actor — used to filter out self-activity
}

export interface PrayerUnreadResult {
  /** Unique unseen prayerIds per groupId. */
  byGroup: Record<string, number>;
  totalUnread: number;
  markGroupPrayersSeen: (groupId: string) => void;
}

function isActivityVisible(privacyLevel: string, isLeader: boolean): boolean {
  if (privacyLevel === 'public') return true;
  if (privacyLevel === 'private') return false;
  return isLeader; // leaders_only
}

export function usePrayerUnread(): PrayerUnreadResult {
  const { organizations, isAuthenticated } = useAuth();
  const [byGroup, setByGroup] = useState<Record<string, number>>({});

  // Re-subscribe when the tab becomes visible — Firestore listeners can go
  // stale while the tab is hidden (mobile uses AppState for the same fix).
  const [visibilityCount, setVisibilityCount] = useState(0);
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') setVisibilityCount((c) => c + 1);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // groupId → leader status, resolved per-ORG (not the current-org `role`):
  // a group_leader in org A must not see leaders_only badges in org B.
  const groupLeader = new Map<string, boolean>();
  for (const org of organizations) {
    const isLeader = (org.roles ?? []).some((r) => LEADER_ROLES.includes(r.role));
    for (const group of org.groups ?? []) groupLeader.set(group.id, isLeader);
  }
  const groupIdsKey = Array.from(groupLeader.keys()).sort().join(',');

  const activityDataRef = useRef(new Map<string, PrayerActivity[]>());
  const receiptDataRef = useRef(new Map<string, string | null>());

  useEffect(() => {
    const firebaseUid = auth.currentUser?.uid;
    if (!isAuthenticated || !firebaseUid || groupLeader.size === 0) {
      setByGroup({});
      return;
    }

    const groupIds = Array.from(groupLeader.keys());

    const recompute = () => {
      const next: Record<string, number> = {};
      for (const groupId of groupIds) {
        const activities = activityDataRef.current.get(groupId) || [];
        const lastSeenAt = receiptDataRef.current.get(groupId) || null;
        const lastSeenTime = lastSeenAt ? new Date(lastSeenAt).getTime() : 0;
        const isLeader = groupLeader.get(groupId) ?? false;

        const visibleUnseen = activities.filter((a) => {
          if (a.userId && a.userId === firebaseUid) return false; // own actions
          if (!isActivityVisible(a.privacyLevel, isLeader)) return false;
          if (!lastSeenAt) return true;
          // 500ms tolerance — receipts use client time vs backend ISO stamps
          return new Date(a.at).getTime() > lastSeenTime + 500;
        });

        // One prayer = one badge, regardless of how many activities it has
        next[groupId] = new Set(visibleUnseen.map((a) => a.prayerId)).size;
      }
      setByGroup(next);
    };

    const unsubscribers: Array<() => void> = [];
    for (const groupId of groupIds) {
      unsubscribers.push(
        onSnapshot(
          doc(db, PRAYER_GROUP_ACTIVITY_COLLECTION, groupId),
          (snap) => {
            activityDataRef.current.set(
              groupId,
              snap.exists() ? snap.data()?.recentActivities || [] : []
            );
            recompute();
          },
          (err) => {
            console.warn(`[usePrayerUnread] Activity listener error for ${groupId}:`, err.message);
          }
        )
      );

      unsubscribers.push(
        onSnapshot(
          doc(db, PRAYER_READ_RECEIPTS_COLLECTION, `${firebaseUid}_${groupId}`),
          (snap) => {
            receiptDataRef.current.set(groupId, snap.exists() ? snap.data()?.lastSeenAt || null : null);
            recompute();
          },
          (err) => {
            console.warn(`[usePrayerUnread] Receipt listener error for ${groupId}:`, err.message);
          }
        )
      );
    }

    return () => {
      unsubscribers.forEach((unsub) => unsub());
      activityDataRef.current.clear();
      receiptDataRef.current.clear();
    };
    // groupIdsKey covers membership changes; visibilityCount forces re-subscribe
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, groupIdsKey, visibilityCount]);

  const markGroupPrayersSeen = useCallback((groupId: string) => {
    const firebaseUid = auth.currentUser?.uid;
    if (!firebaseUid) return;
    // Byte-for-byte the Expo receipt contract: ISO lastSeenAt + serverTimestamp
    // updatedAt, merged — mobile and web share read state.
    setDoc(
      doc(db, PRAYER_READ_RECEIPTS_COLLECTION, `${firebaseUid}_${groupId}`),
      {
        userId: firebaseUid,
        groupId,
        lastSeenAt: new Date().toISOString(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    ).catch((err) => {
      console.warn('[usePrayerUnread] Failed to write receipt:', err.message);
    });
  }, []);

  const totalUnread = Object.values(byGroup).reduce((sum, n) => sum + n, 0);

  return { byGroup, totalUnread, markGroupPrayersSeen };
}
